/**
 * Polyphony Linter
 *
 * Static analysis of a Project to find sections where polyphony exceeds
 * the S-DSP's 8-voice limit.
 */

import type { Project } from '../model/types.js';

/**
 * A polyphony warning for a specific time range.
 */
export interface PolyphonyWarning {
  readonly type: 'polyphony-exceeded';
  readonly startBeat: number;
  readonly endBeat: number;
  readonly maxVoices: number; // Peak voice count in this range
  readonly trackIds: readonly string[]; // Tracks contributing to the overload
}

/**
 * A note event for timeline analysis.
 */
interface NoteEvent {
  readonly type: 'on' | 'off';
  readonly time: number; // In beats (global timeline)
  readonly trackId: string;
  readonly noteIndex: number; // For debugging
}

/**
 * Lint a project for polyphony issues.
 *
 * @param project The project to analyze
 * @param maxVoices Maximum allowed simultaneous voices (default 8)
 * @returns Array of warnings for sections exceeding the limit
 */
export function lintPolyphony(project: Project, maxVoices: number = 8): PolyphonyWarning[] {
  const events = collectNoteEvents(project);

  if (events.length === 0) {
    return [];
  }

  // Sort by time, with 'off' events before 'on' events at same time
  // This handles notes that end exactly when another starts
  events.sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time;
    return a.type === 'off' ? -1 : 1;
  });

  const warnings: PolyphonyWarning[] = [];
  let currentVoices = 0;
  let activeTrackIds = new Set<string>();

  // For tracking sections where polyphony is exceeded
  let warningStartBeat: number | null = null;
  let warningMaxVoices = 0;
  let warningTrackIds = new Set<string>();

  for (const event of events) {
    if (event.type === 'on') {
      currentVoices++;
      activeTrackIds.add(event.trackId);

      if (currentVoices > maxVoices) {
        // Start or continue a warning section
        if (warningStartBeat === null) {
          warningStartBeat = event.time;
          warningTrackIds = new Set(activeTrackIds);
        } else {
          // Update tracks contributing to the problem
          for (const trackId of activeTrackIds) {
            warningTrackIds.add(trackId);
          }
        }
        warningMaxVoices = Math.max(warningMaxVoices, currentVoices);
      }
    } else {
      currentVoices--;

      // Check if we've dropped back to acceptable polyphony
      if (currentVoices <= maxVoices && warningStartBeat !== null) {
        // End the warning section
        warnings.push({
          type: 'polyphony-exceeded',
          startBeat: warningStartBeat,
          endBeat: event.time,
          maxVoices: warningMaxVoices,
          trackIds: Array.from(warningTrackIds),
        });
        warningStartBeat = null;
        warningMaxVoices = 0;
        warningTrackIds = new Set();
      }

      // Note: We don't remove from activeTrackIds because a track might
      // still have other notes playing
    }
  }

  // Handle case where the piece ends while still exceeding polyphony
  if (warningStartBeat !== null) {
    const lastEvent = events[events.length - 1];
    warnings.push({
      type: 'polyphony-exceeded',
      startBeat: warningStartBeat,
      endBeat: lastEvent.time,
      maxVoices: warningMaxVoices,
      trackIds: Array.from(warningTrackIds),
    });
  }

  return warnings;
}

/**
 * Get the maximum polyphony in a project.
 */
export function getMaxPolyphony(project: Project): number {
  const events = collectNoteEvents(project);

  if (events.length === 0) {
    return 0;
  }

  events.sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time;
    return a.type === 'off' ? -1 : 1;
  });

  let currentVoices = 0;
  let maxVoices = 0;

  for (const event of events) {
    if (event.type === 'on') {
      currentVoices++;
      maxVoices = Math.max(maxVoices, currentVoices);
    } else {
      currentVoices--;
    }
  }

  return maxVoices;
}

/**
 * Get polyphony at a specific beat.
 */
export function getPolyphonyAtBeat(project: Project, beat: number): number {
  let count = 0;

  for (const track of project.tracks) {
    if (track.mute) continue;

    for (const clip of track.clips) {
      // clip.start and clip.duration are already in beats
      const clipStartBeat = clip.start;
      const clipEndBeat = clipStartBeat + clip.duration;

      if (beat < clipStartBeat || beat >= clipEndBeat) continue;

      for (const note of clip.notes) {
        const noteStartBeat = clipStartBeat + note.time;
        const noteEndBeat = noteStartBeat + note.duration;

        if (beat >= noteStartBeat && beat < noteEndBeat) {
          count++;
        }
      }
    }
  }

  return count;
}

/**
 * Collect all note on/off events from a project.
 */
function collectNoteEvents(project: Project): NoteEvent[] {
  const events: NoteEvent[] = [];

  for (const track of project.tracks) {
    // Skip muted tracks
    if (track.mute) continue;

    for (const clip of track.clips) {
      // clip.start is already in beats
      const clipStartBeat = clip.start;

      for (let i = 0; i < clip.notes.length; i++) {
        const note = clip.notes[i];
        const noteStartBeat = clipStartBeat + note.time;
        const noteEndBeat = noteStartBeat + note.duration;

        events.push({
          type: 'on',
          time: noteStartBeat,
          trackId: track.id,
          noteIndex: i,
        });

        events.push({
          type: 'off',
          time: noteEndBeat,
          trackId: track.id,
          noteIndex: i,
        });
      }
    }
  }

  return events;
}

/**
 * Create a polyphony timeline for visualization.
 * Returns an array of [beat, voiceCount] pairs at each change point.
 */
export function getPolyphonyTimeline(project: Project): Array<{ beat: number; voices: number }> {
  const events = collectNoteEvents(project);

  if (events.length === 0) {
    return [];
  }

  events.sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time;
    return a.type === 'off' ? -1 : 1;
  });

  const timeline: Array<{ beat: number; voices: number }> = [];
  let currentVoices = 0;
  let lastTime = -1;

  for (const event of events) {
    if (event.type === 'on') {
      currentVoices++;
    } else {
      currentVoices--;
    }

    // Only add to timeline when time changes
    if (event.time !== lastTime) {
      timeline.push({ beat: event.time, voices: currentVoices });
      lastTime = event.time;
    } else {
      // Update the last entry
      timeline[timeline.length - 1] = { beat: event.time, voices: currentVoices };
    }
  }

  return timeline;
}
